import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "./cn.js";
import { Spinner } from "./Spinner.js";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md";
  loading?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", loading, icon, className, children, disabled, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center gap-1.5 rounded-[var(--radius-md)]",
        "transition-colors duration-[var(--motion-fast)] select-none whitespace-nowrap",
        "disabled:opacity-45 disabled:pointer-events-none",
        size === "sm"
          ? "px-3 py-[7px] text-[length:var(--font-size-sm)]"
          : cn("py-2.5 text-[length:var(--font-size-body)]", variant === "primary" ? "px-[18px]" : "px-4"),
        variant === "primary" &&
          "bg-[var(--color-accent)] font-semibold text-[var(--color-accent-contrast)] hover:bg-[var(--color-accent-hover)]",
        variant === "secondary" &&
          "bg-[var(--color-surface-2)] font-medium text-[var(--color-text-primary)] border border-[var(--color-border-strong)] hover:border-[var(--color-text-faint)]",
        variant === "ghost" &&
          "bg-transparent font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-neutral-tint)] hover:text-[var(--color-text-primary)]",
        variant === "danger" && "bg-[var(--color-danger)] font-semibold text-white hover:opacity-90",
        className,
      )}
      {...props}
    >
      {loading ? <Spinner size={14} /> : icon}
      {children}
    </button>
  );
});
